import { PanelHeader } from '@app/components/base/content-structure/PanelHeader';
import { blinkerAnimation } from '@app/core/ui/animations';
import { getSubtleSurfaceBackground } from '@app/core/ui/subtle-surface';
import { usePostHogCaptureLiveTopics } from '@app/modules/logger/screens/stream/features/live-topics/hooks/usePostHogCaptureLiveTopics';
import AssistantIcon from '@mui/icons-material/Assistant';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { Box, IconButton } from '@mui/material';

export interface LiveTopicsPanelHeaderProps {
  title: string;
  isDetailView: boolean;
  onBack: () => void;
  onClose: () => void;
}

/**
 * Live Topics drawer header: wraps PanelHeader with drawer chrome (subtle surface, padding)
 * and domain actions (back in detail, animated AI icon, close + PostHog).
 */
export function LiveTopicsPanelHeader({
  title,
  isDetailView,
  onBack,
  onClose,
}: LiveTopicsPanelHeaderProps) {
  const capture = usePostHogCaptureLiveTopics();

  const handleBack = () => {
    capture('detail_back');
    onBack();
  };

  const handleClose = () => {
    capture('panel_closed', { view: isDetailView ? 'detail' : 'list' });
    onClose();
  };

  return (
    <Box
      sx={{
        px: 1.5,
        pt: 1.5,
        flexShrink: 0,
        backgroundColor: getSubtleSurfaceBackground,
        // Full-width hairline on the outer shell (padding would inset PanelHeader's own border).
        borderBottom: '0.5px solid',
        borderColor: 'divider',
        '& > *': { mb: 0, pb: 1.5 },
      }}
    >
      <PanelHeader
        divider={false}
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            {isDetailView ? (
              <IconButton size="small" onClick={handleBack} sx={{ ml: -0.5 }}>
                <KeyboardBackspaceIcon fontSize="small" />
              </IconButton>
            ) : null}
            <AssistantIcon
              fontSize="small"
              color="info"
              sx={{
                animation: blinkerAnimation,
                flexShrink: 0,
              }}
            />
            {title}
          </Box>
        }
        actions={
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />
    </Box>
  );
}
